import React, { useState, useRef } from "react";
import {
  X,
  Plus,
  Upload,
  Image,
  FileText,
  Tag,
  Settings,
  Star,
  Archive,
  Trash2,
  ArrowUp,
  ArrowDown,
  GripVertical,
} from "lucide-react";
import { resolveNewArchiveIds } from "../utils/archiveMessageIds";

const AddTorrentModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "tutorials",
    size: "",
    is_exclusive: false,
    tracker_replacement_enabled: true,
  });
  const [imageUrls, setImageUrls] = useState([""]);

  const [torrentFile, setTorrentFile] = useState(null);
  const [archiveMessageIds, setArchiveMessageIds] = useState([]);
  const [newArchiveId, setNewArchiveId] = useState("");
  const [archiveError, setArchiveError] = useState(null);
  const archiveInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const categories = [
    { id: "tutorials", label: "Tutorials" },
    { id: "apps", label: "Apps" },
    { id: "plugins", label: "Plugins" },
    { id: "models", label: "Models" },
    { id: "misc", label: "Misc" },
    { id: "materials", label: "Materials" },
    { id: "gamedev", label: "GameDev" },
  ];

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Очищаем ошибку для этого поля
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith(".torrent")) {
        setErrors((prev) => ({
          ...prev,
          torrent_file: "Файл должен иметь расширение .torrent",
        }));
        return;
      }
      setTorrentFile(file);
      setErrors((prev) => ({
        ...prev,
        torrent_file: null,
        content: null,
      }));
    }
  };

  const handleImageUrlChange = (index, value) => {
    setImageUrls((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });

    if (errors.image_urls) {
      setErrors((prev) => ({ ...prev, image_urls: null }));
    }
  };

  const handleAddImageField = () => {
    setImageUrls((prev) => {
      if (prev.length >= 5) {
        return prev;
      }

      return [...prev, ""];
    });
  };

  const handleRemoveImageField = (index) => {
    setImageUrls((prev) => {
      if (prev.length === 1) {
        return [""];
      }

      return prev.filter((_, currentIndex) => currentIndex !== index);
    });
  };

  const normalizedImageUrls = imageUrls
    .map((imageUrl) => imageUrl.trim())
    .filter(Boolean)
    .slice(0, 5);

  const handleAddArchiveId = () => {
    const { error, ids } = resolveNewArchiveIds(
      newArchiveId,
      archiveMessageIds
    );
    if (ids.length === 0) {
      if (error) setArchiveError(error);
      return;
    }
    setArchiveMessageIds((prev) => [...prev, ...ids]);
    setNewArchiveId("");
    setArchiveError(null);
    if (errors.content) {
      setErrors((prev) => ({ ...prev, content: null }));
    }
    archiveInputRef.current?.focus();
  };

  const handleRemoveArchiveId = (index) => {
    setArchiveMessageIds((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMoveArchiveUp = (index) => {
    if (index === 0) return;
    setArchiveMessageIds((prev) => {
      const arr = [...prev];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      return arr;
    });
  };

  const handleMoveArchiveDown = (index) => {
    if (index >= archiveMessageIds.length - 1) return;
    setArchiveMessageIds((prev) => {
      const arr = [...prev];
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      return arr;
    });
  };

  const handleArchiveKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddArchiveId();
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      category: "tutorials",
      size: "",
      is_exclusive: false,
      tracker_replacement_enabled: true,
    });
    setImageUrls([""]);
    setTorrentFile(null);
    setArchiveMessageIds([]);
    setNewArchiveId("");
    setArchiveError(null);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Название обязательно";
    }

    if (!formData.category) {
      newErrors.category = "Категория обязательна";
    }

    if (!torrentFile && archiveMessageIds.length === 0) {
      newErrors.content =
        "Нужен торрент файл или хотя бы один архив";
      newErrors.torrent_file = "Добавьте торрент файл или архивы";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        image_url: normalizedImageUrls[0] || "",
        image_urls: normalizedImageUrls,
        archive_message_ids: archiveMessageIds,
      };

      if (torrentFile) {
        // Проверяем размер файла
        const maxFileSize = 50 * 1024 * 1024; // 50MB
        if (torrentFile.size > maxFileSize) {
          setErrors({
            submit: `Файл слишком большой. Максимальный размер: ${(
              maxFileSize /
              1024 /
              1024
            ).toFixed(0)}MB`,
          });
          return;
        }

        // Конвертируем файл в base64 безопасным способом
        const fileBuffer = await torrentFile.arrayBuffer();

        // Проверяем, что файл не пустой
        if (fileBuffer.byteLength === 0) {
          setErrors({ submit: "Файл пустой или поврежден" });
          return;
        }

        // Безопасная конвертация в base64 для больших файлов
        let base64Data;
        try {
          const uint8Array = new Uint8Array(fileBuffer);
          let binaryString = "";
          const chunkSize = 8192; // Обрабатываем по 8KB за раз

          for (let i = 0; i < uint8Array.length; i += chunkSize) {
            const chunk = uint8Array.slice(i, i + chunkSize);
            binaryString += String.fromCharCode.apply(null, chunk);
          }

          base64Data = btoa(binaryString);
        } catch (conversionError) {
          console.error("Error converting file to base64:", conversionError);
          setErrors({
            submit: "Ошибка обработки файла: " + conversionError.message,
          });
          return;
        }

        payload.torrent_file_data = base64Data;
        payload.torrent_filename = torrentFile.name;
      }

      const response = await fetch("/api/admin/torrents", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Server error response:", errorText);

        let errorMessage = `Ошибка сервера (${response.status}): ${response.statusText}`;

        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.details || errorMessage;
        } catch (parseError) {
          console.warn("Failed to parse error response as JSON");
          errorMessage = errorText || errorMessage;
        }

        setErrors({ submit: errorMessage });
        return;
      }

      const result = await response.json();

      if (result.success) {
        onSuccess && onSuccess(result.torrent);
        resetForm();
        onClose();
      } else {
        setErrors({
          submit:
            result.error || result.details || "Ошибка при добавлении торрента",
        });
      }
    } catch (error) {
      console.error("Error adding torrent:", error);

      // Более детальная обработка ошибок
      let errorMessage = "Ошибка сети при добавлении торрента";

      if (error.name === "TypeError" && error.message.includes("fetch")) {
        errorMessage =
          "Ошибка соединения с сервером. Проверьте подключение к интернету.";
      } else if (error.name === "SyntaxError") {
        errorMessage = "Ошибка обработки ответа сервера";
      } else if (error.message) {
        errorMessage = `Ошибка: ${error.message}`;
      }

      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Затемненный фон */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Модальное окно */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Upload size={24} />
            Добавить торрент вручную
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Форма */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Название */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FileText size={16} className="inline mr-1" />
              Название *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white ${
                errors.title
                  ? "border-red-500 dark:border-red-500"
                  : "border-gray-300 dark:border-gray-600"
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Введите название торрента"
              disabled={isSubmitting}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-500">{errors.title}</p>
            )}
          </div>

          {/* Описание */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FileText size={16} className="inline mr-1" />
              Описание
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите описание торрента (опционально)"
              disabled={isSubmitting}
            />
          </div>

          {/* Категория */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Tag size={16} className="inline mr-1" />
              Категория *
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleInputChange("category", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white ${
                errors.category
                  ? "border-red-500 dark:border-red-500"
                  : "border-gray-300 dark:border-gray-600"
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              disabled={isSubmitting}
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.label}
                </option>
              ))}
            </select>
            {errors.category && (
              <p className="mt-1 text-sm text-red-500">{errors.category}</p>
            )}
          </div>

          {/* URL картинок */}
          <div>
            <div className="flex items-center justify-between mb-2 gap-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <Image size={16} className="inline mr-1" />
                URL картинок
              </label>
              <button
                type="button"
                onClick={handleAddImageField}
                disabled={isSubmitting || imageUrls.length >= 5}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Plus size={14} />
                Добавить
              </button>
            </div>

            <div className="space-y-3">
              {imageUrls.map((imageUrl, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-24 shrink-0 text-sm font-medium text-gray-600 dark:text-gray-300">
                    {index + 1}.{index === 0 ? " (основная)" : ""}
                  </div>
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) =>
                      handleImageUrlChange(index, e.target.value)
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`https://example.com/image-${index + 1}.jpg`}
                    disabled={isSubmitting}
                  />
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveImageField(index)}
                      disabled={isSubmitting}
                      className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 hover:text-red-500 hover:border-red-400 dark:hover:border-red-500 transition-colors disabled:opacity-40"
                      aria-label={`Удалить картинку ${index + 1}`}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Можно добавить до 5 изображений. Первая картинка будет главной, а
              в TOP и режимах grid/list будет использоваться только она.
            </p>
          </div>

          {/* Размер */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Размер
            </label>
            <input
              type="text"
              value={formData.size}
              onChange={(e) => handleInputChange("size", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="например: 1.5GB (если не указано, будет вычислен автоматически)"
              disabled={isSubmitting}
            />
          </div>

          {/* Торрент файл */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Upload size={16} className="inline mr-1" />
              Торрент файл
            </label>
            <input
              type="file"
              accept=".torrent"
              onChange={handleFileChange}
              className={`w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white ${
                errors.torrent_file
                  ? "border-red-500 dark:border-red-500"
                  : "border-gray-300 dark:border-gray-600"
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              disabled={isSubmitting}
            />
            {errors.torrent_file && (
              <p className="mt-1 text-sm text-red-500">{errors.torrent_file}</p>
            )}
            {torrentFile && (
              <p className="mt-1 text-sm text-green-600 dark:text-green-400">
                Выбран файл: {torrentFile.name}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Нужен торрент файл или хотя бы один архив ниже
            </p>
          </div>

          {/* Архивы */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Archive size={16} className="inline mr-1" />
              Архивы
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Добавьте Telegram message ID из archive channel. Каждый ID — одна
              часть файла. Можно указать диапазон:{" "}
              <span className="font-mono text-gray-400 dark:text-gray-300">
                120:5
              </span>{" "}
              добавит 120–124.
            </p>

            {archiveMessageIds.length === 0 ? (
              <div
                className={`text-center text-gray-500 dark:text-gray-400 py-6 border border-dashed rounded-xl mb-4 ${
                  errors.content
                    ? "border-red-500 dark:border-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                Архивы ещё не добавлены
              </div>
            ) : (
              <div className="space-y-2 mb-4">
                {archiveMessageIds.map((id, index) => (
                  <div
                    key={`${id}-${index}`}
                    className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-600"
                  >
                    <GripVertical
                      size={14}
                      className="text-gray-400 flex-shrink-0"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 flex-1 font-mono">
                      Part {index + 1} — msg_id: {id}
                    </span>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => handleMoveArchiveUp(index)}
                        disabled={isSubmitting || index === 0}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 hover:text-gray-800 dark:hover:text-white transition-colors disabled:opacity-30"
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveArchiveDown(index)}
                        disabled={
                          isSubmitting ||
                          index === archiveMessageIds.length - 1
                        }
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 hover:text-gray-800 dark:hover:text-white transition-colors disabled:opacity-30"
                      >
                        <ArrowDown size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveArchiveId(index)}
                        disabled={isSubmitting}
                        className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/40 text-gray-500 hover:text-red-500 transition-colors disabled:opacity-40"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <input
                ref={archiveInputRef}
                type="text"
                inputMode="text"
                value={newArchiveId}
                onChange={(e) => setNewArchiveId(e.target.value)}
                onKeyDown={handleArchiveKeyDown}
                placeholder="120 or 120:5"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={handleAddArchiveId}
                disabled={isSubmitting}
                className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center gap-1 text-sm font-medium disabled:opacity-50"
              >
                <Plus size={16} />
                Add
              </button>
            </div>

            {archiveError && (
              <p className="mt-2 text-xs text-red-500">{archiveError}</p>
            )}
            {errors.content && (
              <p className="mt-2 text-sm text-red-500">{errors.content}</p>
            )}
          </div>

          {/* Настройки */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <Settings size={18} />
              Настройки
            </h3>

            {/* Эксклюзивный торрент */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_exclusive"
                checked={formData.is_exclusive}
                onChange={(e) =>
                  handleInputChange("is_exclusive", e.target.checked)
                }
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                disabled={isSubmitting}
              />
              <label
                htmlFor="is_exclusive"
                className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1"
              >
                <Star size={16} className="text-yellow-500" />
                Эксклюзивный торрент
              </label>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
              Эксклюзивные торренты отображаются с особым стилем (золотая лента,
              свечение)
            </p>

            {/* Подмена трекера */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="tracker_replacement"
                checked={formData.tracker_replacement_enabled}
                onChange={(e) =>
                  handleInputChange(
                    "tracker_replacement_enabled",
                    e.target.checked,
                  )
                }
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                disabled={isSubmitting}
              />
              <label
                htmlFor="tracker_replacement"
                className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Подменять трекер при отправке
              </label>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
              Если включено, трекер будет автоматически заменен на рабочий. Если
              выключено, торрент отправляется как есть.
            </p>
          </div>

          {/* Ошибка отправки */}
          {errors.submit && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg dark:bg-red-900/30 dark:border-red-700 dark:text-red-400">
              {errors.submit}
            </div>
          )}

          {/* Кнопки */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Добавление...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Добавить торрент
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTorrentModal;
